"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import {
  Home,
  Zap,
  LayoutGrid,
  Network,
  Trophy,
  Menu,
  Wallet,
  ChevronRight,
  Share2,
  Flame,
  User,
} from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { BlockiesAvatar } from "@/components/blockies-avatar";
import { UserSettingsModal } from "@/components/user-settings-modal";
import { cn } from "@/lib/utils";
import { images } from "@/config/images";

const navItems = [
  {
    label: "Home",
    href: "/",
    icon: Home,
  },
  {
    label: "Apps",
    href: "/apps",
    icon: LayoutGrid,
  },
  {
    label: "Campaigns",
    href: "/campaigns",
    icon: Zap,
  },
  {
    label: "Leaderboard",
    href: "/leaderboard",
    icon: Trophy,
  },
  {
    label: "Referral",
    href: "/referral",
    icon: Share2,
  },
  {
    label: "Profile",
    href: "/profile",
    icon: User,
  },
];

export function SpicenetNavigation() {
  const pathname = usePathname();
  const { address, isAuthenticated, openConnectModal, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const formatAddress = (addr: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-[#E1DDD8] flex-col py-8 px-6 z-50">
        {/* Logo */}
        <div className="mb-12">
          <div className="flex items-center gap-3">
            <Image
              src={images.logo.text}
              alt="Spicenet"
              width={150}
              height={40}
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 flex flex-col gap-2">
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  isActive
                    ? "text-black bg-white/50"
                    : "text-muted-light hover:text-black hover:bg-white/30"
                )}
              >
                <item.icon
                  className="w-6 h-6"
                  strokeWidth={isActive ? 2.5 : 2}
                />
                <span className="text-base font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Avatar or Connect Button */}
        <div className="pt-4">
          {isAuthenticated ? (
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-transparent hover:border-spicenet-primary transition-colors bg-white/30"
            >
              <BlockiesAvatar
                seed={address || ""}
                size={10}
                scale={4}
                className=""
              />
              <div className="flex-1 text-left">
                <p className="text-xs text-muted-light uppercase">Connected</p>
                {address && (
                  <p className="text-sm font-medium text-black">
                    {formatAddress(address)}
                  </p>
                )}
              </div>
            </button>
          ) : (
            <button
              onClick={openConnectModal}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-spicenet-gray rounded-lg hover:bg-[#1A1917] transition-colors"
            >
              <Wallet className="w-5 h-5 text-white" strokeWidth={2} />
              <span className="text-sm font-medium text-white">Log In</span>
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Header Bar - Always visible on mobile, positioned below the devnet banner */}
      <header className="md:hidden fixed top-10 left-0 right-0 h-16 bg-[#E1DDD8] flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <Image
            src={images.logo.main}
            alt="Spicenet"
            width={28}
            height={28}
            className="w-7 h-7 object-contain"
          />
          <span className="text-lg font-medium text-black">Spicenet</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-10 h-10 flex items-center justify-center"
        >
          <Menu className="w-7 h-7 text-black" strokeWidth={2} />
        </button>
      </header>

      {/* Mobile Navigation - Full Screen */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="md:hidden fixed inset-0 bg-black/20 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              className="md:hidden fixed inset-0 bg-[#E1DDD8] z-[60]"
            >
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="flex items-center justify-between p-4 h-16">
                  <div className="flex items-center gap-2">
                    <Image
                      src={images.logo.main}
                      alt="Spicenet"
                      width={28}
                      height={28}
                      className="w-7 h-7 object-contain"
                    />
                    <span className="text-lg font-medium text-black">
                      Spicenet
                    </span>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center"
                  >
                    <ChevronRight
                      className="w-6 h-6 text-black"
                      strokeWidth={2}
                    />
                  </button>
                </div>

                {/* Navigation Items */}
                <nav className="flex-1 px-6">
                  <div className="space-y-2">
                    {navItems.map((item) => {
                      const isActive = pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-4 px-4 py-4 transition-all duration-200 font-medium tracking-[-1.5px]",
                            isActive ? "text-black" : "text-muted-light"
                          )}
                        >
                          <item.icon
                            className="w-7 h-7"
                            strokeWidth={isActive ? 2.5 : 2}
                          />
                          <span className="text-lg">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </nav>

                {/* Wallet Section */}
                <div className="p-6">
                  {isAuthenticated ? (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setSettingsOpen(true);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-4 bg-white/50 rounded-lg hover:bg-white/70 transition-colors"
                    >
                      <BlockiesAvatar
                        seed={address || ""}
                        size={10}
                        scale={4}
                        className=""
                      />
                      <div className="flex-1 text-left">
                        <p className="text-xs text-muted-light uppercase">
                          Connected
                        </p>
                        {address && (
                          <p className="text-sm font-medium text-black">
                            {formatAddress(address)}
                          </p>
                        )}
                      </div>
                      <div className="w-2 h-2 bg-spicenet-primary rounded-full" />
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        openConnectModal();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-4 bg-spicenet-black text-white rounded-lg hover:bg-[#1A1917] transition-colors"
                    >
                      <Wallet className="w-6 h-6" strokeWidth={2} />
                      <span className="text-lg font-medium">Log In</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* User Settings Modal */}
      <UserSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}
